import React from "react";
import { Link } from "react-router-dom";
import PublisherAttribution from "../components/PublisherAttribution";
import Seo from "../components/Seo";
import {
  BookOpen,
  Books as BooksIcon,
  ChalkboardTeacher,
  GraduationCap,
  Presentation,
  Microphone,
  Buildings,
  Handshake,
  FilePdf,
  Church,
  UsersThree,
  Quotes,
  EnvelopeSimple,
  Sparkle,
  Compass,
  ShoppingCart,
  Package,
} from "@phosphor-icons/react";

/**
 * From Diagnosis to Goodbye — flagship educational platform landing page.
 *
 * Hero + Flagship Book are populated with the published book.
 * Remaining curriculum, training, seminar, and testimonial sections
 * remain "In Preparation" until real content is supplied.
 *
 * -------- To finish populating the flagship book --------
 * Fill in the two empty strings below (`amazonUrl`, `coverImage`) with the
 * final Amazon product URL and the direct cover image URL. Once both are
 * set, the "Buy on Amazon" button activates and the real cover replaces
 * the styled placeholder — no other code changes needed.
 */

const BRAND = "From Diagnosis to Goodbye";

const FLAGSHIP_BOOK = {
  title: BRAND,
  subtitle: "Restoring the Patient\u2019s Voice Through Every Stage of the Journey",
  author: "Derrick Hall",
  // Official Amazon paperback listing (opens in a new tab).
  amazonUrl:
    "https://www.amazon.com/Diagnosis-Goodbye-Restoring-Patients-Through/dp/0980212723/ref=tmm_pap_swatch_0",
  // Official high-resolution cover from Amazon media (1000x1599).
  coverImage: "https://m.media-amazon.com/images/I/71pLPPxlr5L.jpg",
};

const JUMP_NAV = [
  { id: "about", label: "About the Program" },
  { id: "flagship-book", label: "The Book" },
  { id: "workbooks", label: "Workbooks" },
  { id: "facilitator-guides", label: "Facilitator Guides" },
  { id: "participant-guides", label: "Participant Guides" },
  { id: "reflection-cards", label: "Reflection Cards" },
  { id: "seminars", label: "Seminars & Workshops" },
  { id: "hospice-training", label: "Hospice Training" },
  { id: "church-training", label: "Church & Community" },
  { id: "speaking", label: "Speaking" },
  { id: "licensing", label: "Licensing" },
  { id: "testimonials", label: "Testimonials" },
  { id: "contact", label: "Book Derrick Hall" },
];

const InPreparationPill = () => (
  <span
    data-testid="fdtg-in-prep-pill"
    className="inline-flex items-center gap-1.5 bg-secondary/10 text-secondary-dark text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full font-semibold border border-secondary/20"
  >
    <Sparkle size={11} weight="fill" />
    In Preparation
  </span>
);

const SectionShell = ({ id, overline, title, icon: Icon, children, testId }) => (
  <section
    id={id}
    data-testid={testId || `fdtg-section-${id}`}
    className="scroll-mt-28 py-16 sm:py-20 border-t border-ink-100 first:border-t-0"
  >
    <div className="container-x">
      <div className="max-w-3xl">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
          <span className="w-8 h-px bg-secondary" />
          {overline}
        </span>
        <div className="mt-5 flex items-start gap-4">
          {Icon && (
            <span className="hidden sm:inline-flex items-center justify-center w-14 h-14 shrink-0 rounded-2xl bg-primary/8 text-primary border border-primary/10">
              <Icon size={26} weight="duotone" />
            </span>
          )}
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.1]">
            {title}
          </h2>
        </div>
      </div>
      <div className="mt-8 lg:mt-10">{children}</div>
    </div>
  </section>
);

const PlaceholderCard = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="bg-white border border-ink-100 rounded-3xl p-8 sm:p-10 shadow-[0_18px_60px_-24px_rgba(15,23,42,0.08)]"
  >
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <InPreparationPill />
      <span className="text-xs uppercase tracking-[0.18em] text-ink-400 font-semibold">
        Coming Soon
      </span>
    </div>
    <div className="mt-5 text-ink-700 leading-relaxed">{children}</div>
  </div>
);

/**
 * Populated content card (no "In Preparation" or "Coming Soon" labels) for
 * sections that describe established, active program offerings.
 */
const ContentCard = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="bg-white border border-ink-100 rounded-3xl p-8 sm:p-10 shadow-[0_18px_60px_-24px_rgba(15,23,42,0.08)]"
  >
    <div className="space-y-5 text-ink-700 leading-relaxed">{children}</div>
  </div>
);

/**
 * Shared CTA cluster for the flagship book. Renders three actions:
 *  1. Buy on Amazon — opens the official listing in a new tab
 *     (target="_blank" rel="noopener noreferrer"). Disabled state
 *     appears until FLAGSHIP_BOOK.amazonUrl is provided, so no
 *     invented link is ever surfaced to users.
 *  2. Request a Workshop — routes to the ministry contact page.
 *  3. Bulk Orders — routes to the dedicated /bulk-orders page.
 */
const BookCTAs = ({ testIdPrefix }) => {
  const hasAmazonUrl = Boolean(FLAGSHIP_BOOK.amazonUrl);
  return (
    <div className="flex flex-wrap items-center gap-3 sm:gap-4">
      {hasAmazonUrl ? (
        <a
          href={FLAGSHIP_BOOK.amazonUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testIdPrefix}-buy-amazon`}
          aria-label={`Buy ${FLAGSHIP_BOOK.title} on Amazon (opens in a new tab)`}
          className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold text-base hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
        >
          <ShoppingCart size={18} weight="duotone" />
          Buy on Amazon
        </a>
      ) : (
        <button
          type="button"
          disabled
          data-testid={`${testIdPrefix}-buy-amazon`}
          aria-label="Buy on Amazon (link coming soon)"
          className="inline-flex items-center gap-2 bg-primary/40 text-white rounded-full px-7 py-3.5 font-semibold text-base cursor-not-allowed"
          title="Amazon link will be added shortly"
        >
          <ShoppingCart size={18} weight="duotone" />
          Buy on Amazon
        </button>
      )}
      <Link
        to="/contact?topic=workshop-request"
        data-testid={`${testIdPrefix}-request-workshop`}
        className="inline-flex items-center gap-2 border-2 border-primary text-primary rounded-full px-6 py-3.5 font-semibold hover:bg-primary/5 transition-colors"
      >
        <ChalkboardTeacher size={18} weight="duotone" />
        Request a Workshop
      </Link>
      <Link
        to="/bulk-orders"
        data-testid={`${testIdPrefix}-bulk-orders`}
        className="inline-flex items-center gap-2 text-ink-700 hover:text-primary text-sm font-semibold underline underline-offset-4 decoration-primary/30 hover:decoration-primary transition-colors"
      >
        <Package size={16} weight="duotone" />
        Bulk Orders
      </Link>
    </div>
  );
};

/**
 * Book cover — renders the real cover image when FLAGSHIP_BOOK.coverImage
 * is set; otherwise falls back to a styled brand placeholder with an
 * intentional "Cover coming soon" label.
 */
const BookCover = ({ testId, className = "" }) => {
  if (FLAGSHIP_BOOK.coverImage) {
    return (
      <img
        src={FLAGSHIP_BOOK.coverImage}
        alt={`${FLAGSHIP_BOOK.title} — book cover`}
        data-testid={testId}
        loading="lazy"
        className={`aspect-[3/4] w-full rounded-2xl object-cover border border-ink-100 shadow-[0_24px_70px_-24px_rgba(15,23,42,0.28)] ${className}`}
      />
    );
  }
  return (
    <div
      data-testid={testId}
      role="img"
      aria-label={`${FLAGSHIP_BOOK.title} — cover coming soon`}
      className={`aspect-[3/4] w-full rounded-2xl bg-gradient-to-br from-primary/12 via-white to-secondary/20 border border-ink-100 shadow-[0_24px_70px_-24px_rgba(15,23,42,0.22)] flex flex-col items-center justify-center text-center p-8 ${className}`}
    >
      <BooksIcon size={44} weight="duotone" className="text-primary" />
      <p className="mt-5 font-heading text-xl text-ink-800 leading-snug">
        {FLAGSHIP_BOOK.title}
      </p>
      <p className="mt-2 text-sm text-ink-600 italic leading-snug">
        {FLAGSHIP_BOOK.subtitle}
      </p>
      <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-ink-500 font-semibold">
        Cover Coming Soon
      </p>
    </div>
  );
};

const FromDiagnosisToGoodbye = () => {
  return (
    <div data-testid="page-fdtg">
      <Seo
        title="From Diagnosis to Goodbye — Educational Platform"
        description="A companion journey for families facing serious illness. Faith-based hospice guidance, caregiver support, and end-of-life spiritual care by Derrick Hall."
      />
      {/* 1. HERO */}
      <section
        id="top"
        data-testid="fdtg-hero"
        className="relative pt-24 pb-20 sm:pt-32 sm:pb-28 overflow-hidden"
      >
        {/* Warm ambient background */}
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-gradient-to-br from-softblue/60 via-white to-secondary/5"
        />
        <div
          aria-hidden="true"
          className="absolute -top-20 -right-20 w-[520px] h-[520px] rounded-full bg-secondary/10 blur-3xl -z-10"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-24 -left-16 w-[420px] h-[420px] rounded-full bg-primary/10 blur-3xl -z-10"
        />

        <div className="container-x">
          <div className="grid lg:grid-cols-[1fr,320px] xl:grid-cols-[1fr,360px] gap-12 lg:gap-16 items-center">
            <div>
              <span
                data-testid="fdtg-hero-eyebrow"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold"
              >
                <span className="w-8 h-px bg-secondary" />
                The Flagship Book &amp; Educational Platform
              </span>
              <h1
                data-testid="fdtg-hero-title"
                className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] text-ink-900"
              >
                {FLAGSHIP_BOOK.title}
              </h1>
              <p
                data-testid="fdtg-hero-subtitle"
                className="mt-5 font-heading text-xl sm:text-2xl text-ink-700 italic leading-snug max-w-2xl"
              >
                {FLAGSHIP_BOOK.subtitle}
              </p>
              <p
                data-testid="fdtg-hero-author"
                className="mt-4 text-sm uppercase tracking-[0.22em] text-primary font-semibold"
              >
                By {FLAGSHIP_BOOK.author}
              </p>

              <div className="mt-8" data-testid="fdtg-hero-ctas">
                <BookCTAs testIdPrefix="fdtg-hero" />
              </div>
            </div>

            <div className="lg:max-w-none max-w-[280px] mx-auto lg:mx-0 w-full">
              <BookCover testId="fdtg-hero-cover" />
            </div>
          </div>

          {/* Jump navigation */}
          <nav
            data-testid="fdtg-jump-nav"
            aria-label="Jump to section"
            className="mt-14 -mx-4 px-4 overflow-x-auto"
          >
            <ul className="flex gap-2 min-w-max pb-2">
              {JUMP_NAV.map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    data-testid={`fdtg-jump-${item.id}`}
                    className="inline-flex items-center px-4 py-2 rounded-full bg-white/70 border border-ink-100 text-sm text-ink-700 hover:text-primary hover:border-primary/30 transition-colors whitespace-nowrap"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </section>

      {/* 2. ABOUT THE PROGRAM */}
      <SectionShell
        id="about"
        overline="About the Program"
        title={`What Is ${BRAND}?`}
        icon={Compass}
      >
        <div
          data-testid="fdtg-about-card"
          className="bg-white border border-ink-100 rounded-3xl p-8 sm:p-10 shadow-[0_18px_60px_-24px_rgba(15,23,42,0.08)]"
        >
          <div className="space-y-5 text-ink-700 leading-relaxed">
            <p>
              <em>{BRAND}</em> is a compassionate educational program designed
              to help individuals, families, caregivers, hospice
              professionals, churches, and communities navigate the
              end-of-life journey with greater understanding, confidence,
              and hope.
            </p>
            <p>
              Rather than focusing only on the final days of life, the
              program begins at the moment of diagnosis and follows the
              entire journey&mdash;helping participants understand the
              emotional, spiritual, relational, and practical challenges
              that arise along the way. Through books, guided workbooks,
              workshops, facilitator resources, and professional training,
              the program encourages meaningful conversations, strengthens
              relationships, and equips people to provide compassionate
              support during one of life&rsquo;s most significant
              transitions.
            </p>
            <p>
              The goal of <em>{BRAND}</em> is not simply to prepare people
              for death, but to help people live each remaining day with
              greater purpose, deeper connection, and the assurance that no
              one should have to walk this journey alone.
            </p>
            <p>
              As the educational platform continues to grow, additional
              resources&mdash;including companion workbooks, facilitator
              guides, participant guides, seminars, and training
              programs&mdash;will expand this mission and provide practical
              tools for families, churches, hospice organizations, and
              community leaders.
            </p>
          </div>

          <div
            data-testid="fdtg-about-platform-note"
            className="mt-8 rounded-2xl bg-softblue/50 border border-primary/10 p-5 text-sm text-ink-700 leading-relaxed"
          >
            <span className="block text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-2">
              More Than a Book
            </span>
            {BRAND} is the educational foundation of a growing platform
            dedicated to helping people navigate one of life&rsquo;s most
            meaningful journeys with compassion, dignity, understanding,
            and hope.
          </div>
        </div>
      </SectionShell>

      {/* 3. FLAGSHIP BOOK */}
      <SectionShell
        id="flagship-book"
        overline="The Flagship Book"
        title={`${BRAND} \u2014 the book`}
        icon={BookOpen}
      >
        <div className="grid lg:grid-cols-[320px,1fr] gap-10 lg:gap-14 items-start">
          <div className="max-w-[320px] w-full mx-auto lg:mx-0">
            <BookCover testId="fdtg-book-cover" />
          </div>
          <div
            data-testid="fdtg-book-card"
            className="bg-white border border-ink-100 rounded-3xl p-8 sm:p-10 shadow-[0_18px_60px_-24px_rgba(15,23,42,0.08)]"
          >
            <h3
              data-testid="fdtg-book-title"
              className="font-heading text-3xl sm:text-4xl text-ink-900 font-medium leading-[1.1]"
            >
              {FLAGSHIP_BOOK.title}
            </h3>
            <p
              data-testid="fdtg-book-subtitle"
              className="mt-3 font-heading text-lg sm:text-xl text-ink-700 italic leading-snug"
            >
              {FLAGSHIP_BOOK.subtitle}
            </p>
            <p
              data-testid="fdtg-book-author"
              className="mt-4 text-xs uppercase tracking-[0.22em] text-primary font-semibold"
            >
              By {FLAGSHIP_BOOK.author}
            </p>

            {FLAGSHIP_BOOK.description ? (
              <p
                data-testid="fdtg-book-description"
                className="mt-6 text-ink-700 leading-relaxed"
              >
                {FLAGSHIP_BOOK.description}
              </p>
            ) : (
              <div
                data-testid="fdtg-book-description"
                className="mt-6 space-y-5 text-ink-700 leading-relaxed"
              >
                <p className="font-heading text-xl text-ink-900 italic leading-snug">
                  What if the greatest need after a terminal diagnosis
                  isn&rsquo;t another medical explanation&mdash;but someone
                  to walk beside you?
                </p>
                <p>
                  A terminal diagnosis changes more than a person&rsquo;s
                  health. It changes conversations, relationships, dreams,
                  and the way families see tomorrow. In the midst of
                  appointments, medications, and difficult decisions, one
                  voice is often overlooked&mdash;the patient&rsquo;s.
                </p>
                <p>
                  <em>{BRAND}</em> is more than a hospice book. It is a
                  compassionate companion for patients, families,
                  caregivers, and healthcare professionals navigating one
                  of life&rsquo;s most sacred journeys.
                </p>
                <p>
                  Drawing from a decade of hospice chaplaincy, Chaplain
                  Derrick Hall gently guides readers through every stage of
                  the end-of-life journey&mdash;from the day of diagnosis
                  to the final goodbye, and beyond. With warmth, honesty,
                  and practical wisdom, this book helps readers understand
                  not only what is happening medically, but also what is
                  happening emotionally, spiritually, and relationally
                  behind the scenes.
                </p>

                <div>
                  <p className="font-semibold text-ink-800">
                    Throughout the book, readers will discover:
                  </p>
                  <ul className="mt-3 space-y-2 list-disc pl-6 marker:text-primary">
                    <li>How to restore the patient&rsquo;s voice during serious illness.</li>
                    <li>Ways to strengthen family communication and navigate difficult conversations.</li>
                    <li>Practical guidance for creating meaningful memories and leaving a lasting legacy.</li>
                    <li>Encouragement for caregivers carrying the weight of responsibility.</li>
                    <li>Gentle explanations of comfort care and common hospice concerns.</li>
                    <li>Compassionate guidance for including children in the journey.</li>
                    <li>Hope-filled support for families living with grief after the loss of a loved one.</li>
                    <li>Practical appendices with questions, legacy activities, comfort resources, prayers, and reflection pages.</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold text-ink-800">
                    Each chapter includes five unique features that make
                    this book unlike traditional hospice resources:
                  </p>
                  <ul className="mt-3 space-y-2 pl-0 list-none">
                    {[
                      ["The Unseen Voice", "Giving words to what patients may be feeling but are unable to say."],
                      ["The Chaplain\u2019s Observation", "Real-life lessons from years of bedside ministry."],
                      ["Behind the Scenes", "Understanding the hidden emotions affecting patients and families."],
                      ["Derrick\u2019s Notebook", "Personal reflections from the heart of a hospice chaplain."],
                      ["Walking Forward Together", "Gentle encouragement and practical next steps for every stage of the journey."],
                    ].map(([name, desc]) => (
                      <li key={name} className="flex gap-3">
                        <span className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-secondary" aria-hidden="true" />
                        <span>
                          <span className="font-semibold text-ink-800">{name}</span>
                          <span className="text-ink-700"> &mdash; {desc}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <p>
                  Whether you are the patient, a spouse, an adult child, a
                  grandchild, a caregiver, a hospice professional, or a
                  friend searching for the right words, this book offers
                  understanding, comfort, and hope.
                </p>
                <p className="font-heading text-lg text-primary italic">
                  Because no one should have to walk the journey from
                  diagnosis to goodbye alone.
                </p>

                <div
                  data-testid="fdtg-book-platform-note"
                  className="mt-6 rounded-2xl bg-softblue/50 border border-primary/10 p-5 text-sm text-ink-700 leading-relaxed"
                >
                  <span className="block text-[10px] uppercase tracking-[0.22em] text-primary font-semibold mb-2">
                    The Foundation of the Platform
                  </span>
                  {BRAND} is more than a single book&mdash;it is the
                  cornerstone of a growing educational platform of
                  workbooks, facilitator guides, participant guides,
                  seminars, and organizational training for families,
                  caregivers, hospice professionals, churches, and
                  communities.
                </div>
              </div>
            )}

            <div className="mt-8" data-testid="fdtg-book-ctas">
              <BookCTAs testIdPrefix="fdtg-book" />
            </div>
          </div>
        </div>
      </SectionShell>

      {/* 4. COMING SOON: GUIDED WORKBOOKS */}
      <section
        id="workbooks"
        data-testid="fdtg-section-workbooks"
        className="scroll-mt-28 py-16 sm:py-20 border-t border-ink-100"
        aria-labelledby="fdtg-workbooks-heading"
      >
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Companion Curriculum
            </span>
            <div className="mt-5 flex items-start gap-4">
              <span className="hidden sm:inline-flex items-center justify-center w-14 h-14 shrink-0 rounded-2xl bg-primary/8 text-primary border border-primary/10">
                <BooksIcon size={26} weight="duotone" />
              </span>
              <h2
                id="fdtg-workbooks-heading"
                data-testid="fdtg-workbooks-heading"
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.1]"
              >
                Guided Workbooks
              </h2>
            </div>
            <p
              data-testid="fdtg-workbooks-intro"
              className="mt-6 text-ink-700 text-lg leading-relaxed"
            >
              The {BRAND} collection includes two guided workbooks created
              to help individuals and families move from understanding the
              journey to personally engaging with it. Each workbook offers
              compassionate reflections, practical exercises, and meaningful
              conversation prompts that may be used individually, with
              family members, or in a facilitated group.
            </p>
          </div>

          <div
            data-testid="fdtg-workbooks-grid"
            className="mt-12 lg:mt-14 grid md:grid-cols-2 gap-8 lg:gap-12"
          >
            {[
              {
                id: "hospice",
                title: `${BRAND} Hospice Workbook`,
                subtitle:
                  "A Guided Companion for Preparation, Presence, and Family Conversation",
                description:
                  "Designed for individuals and families navigating hospice, this guided workbook creates space for honest conversation, thoughtful preparation, meaningful connection, and compassionate presence throughout the journey.",
                cover:
                  "/workbooks/From_Diagnosis_to_Goodbye_Hospice_Workbook_Front_Cover.png",
                alt:
                  "Cover of the From Diagnosis to Goodbye Hospice Workbook by Derrick Hall",
                amazonUrl: "https://www.amazon.com/dp/0980212731",
              },
              {
                id: "bereavement",
                title: `${BRAND} Bereavement Workbook`,
                subtitle:
                  "A Guided Companion for Reflection, Healing, and Life After Loss",
                description:
                  "Created for individuals and families adjusting to life after a death, this guided workbook provides structured opportunities for reflection, remembrance, emotional expression, and gentle movement toward healing.",
                cover:
                  "/workbooks/From_Diagnosis_to_Goodbye_Bereavement_Workbook_Front_Cover.png",
                alt:
                  "Cover of the From Diagnosis to Goodbye Bereavement Workbook by Derrick Hall",
                amazonUrl: "https://www.amazon.com/dp/098021274X",
              },
            ].map((wb) => (
              <article
                key={wb.id}
                data-testid={`fdtg-workbook-card-${wb.id}`}
                className="flex flex-col bg-white border border-ink-100 rounded-3xl overflow-hidden shadow-[0_18px_60px_-24px_rgba(15,23,42,0.10)] hover:shadow-[0_28px_80px_-24px_rgba(15,23,42,0.18)] transition-shadow duration-300"
              >
                {/* Full cover (no crop) — object-contain preserves proportions */}
                <div className="relative bg-gradient-to-br from-softblue/40 via-white to-secondary/10 border-b border-ink-100 p-6 sm:p-8">
                  <img
                    src={wb.cover}
                    alt={wb.alt}
                    data-testid={`fdtg-workbook-cover-${wb.id}`}
                    loading="lazy"
                    className="w-full h-auto max-h-[520px] object-contain mx-auto drop-shadow-[0_20px_40px_rgba(15,23,42,0.18)] rounded-md"
                  />
                  <span
                    data-testid={`fdtg-workbook-badge-${wb.id}`}
                    aria-label="Status: Available Now"
                    className="absolute top-5 right-5 inline-flex items-center gap-1.5 bg-primary text-white text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1.5 rounded-full shadow-md"
                  >
                    Available Now
                  </span>
                </div>

                <div className="flex-1 flex flex-col p-7 sm:p-8">
                  <h3
                    data-testid={`fdtg-workbook-title-${wb.id}`}
                    className="font-heading text-2xl sm:text-3xl text-ink-900 font-medium leading-snug"
                  >
                    {wb.title}
                  </h3>
                  <p className="mt-3 font-heading text-base sm:text-lg text-ink-700 italic leading-snug">
                    {wb.subtitle}
                  </p>
                  <p className="mt-4 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
                    By {FLAGSHIP_BOOK.author}
                  </p>
                  <p className="mt-5 text-ink-700 leading-relaxed text-sm sm:text-base flex-1">
                    {wb.description}
                  </p>
                  <div className="mt-6">
                    <a
                      href={wb.amazonUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`fdtg-workbook-cta-${wb.id}`}
                      className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
                    >
                      <ShoppingCart size={14} weight="duotone" />
                      Purchase on Amazon
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <p
            data-testid="fdtg-workbooks-closing"
            className="mt-12 text-center text-sm text-ink-500 italic max-w-2xl mx-auto leading-relaxed"
          >
            Both guided workbooks are now available for purchase on Amazon.
          </p>
        </div>
      </section>

      {/* 5. FACILITATOR GUIDES */}
      <SectionShell
        id="facilitator-guides"
        overline="For Group Leaders"
        title="Facilitator Guides"
        icon={ChalkboardTeacher}
      >
        <ContentCard testId="fdtg-facilitator-card">
          <p>
            The <em>{BRAND}</em> Facilitator Guides are designed to equip
            pastors, chaplains, hospice professionals, educators, care
            ministry leaders, and small-group facilitators to confidently
            lead meaningful discussions surrounding serious illness, hospice
            care, caregiving, grief, and compassionate communication.
          </p>
          <p>
            Each guide provides structured session plans, teaching outlines,
            discussion questions, practical activities, facilitator
            insights, optional closing reflection exercises or prayers when
            appropriate, and pacing suggestions to help leaders create
            engaging, supportive, and meaningful learning experiences for a
            variety of audiences.
          </p>
          <p>
            As the educational platform continues to expand, facilitator
            resources will be released for workshops, church studies,
            hospice staff training, caregiver education, and community
            seminars.
          </p>
        </ContentCard>

        {/* Featured Facilitator Guides gallery */}
        <div className="mt-14 lg:mt-16">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Featured Facilitator Guides
            </span>
            <h3
              data-testid="fdtg-facilitator-gallery-heading"
              className="mt-4 font-heading text-2xl sm:text-3xl text-ink-900 font-medium leading-snug"
            >
              A guide for every setting.
            </h3>
          </div>

          <div
            data-testid="fdtg-facilitator-gallery-grid"
            className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
          >
            {[
              {
                id: "combined",
                title: "Combined Facilitator Guide",
                subtitle: "For the Hospice & Bereavement Workbooks",
                description:
                  "A comprehensive facilitator guide designed to lead both the Hospice Workbook and Bereavement Workbook as one integrated educational experience.",
                cover:
                  "/facilitator-guides/FDTG_Facilitator_Combined_Hospice_Bereavement.png",
                alt:
                  "Cover of the From Diagnosis to Goodbye Combined Facilitator Guide by Derrick Hall",
              },
              {
                id: "church",
                title: "Church Small-Group Facilitator Guide",
                subtitle:
                  "For Guided Conversations on Hospice, Caregiving, Grief & Healing",
                description:
                  "Designed for pastors, care ministry leaders, elders, deacons, and small-group facilitators who desire to equip their congregations with compassionate end-of-life education.",
                cover:
                  "/facilitator-guides/FDTG_Facilitator_Church_Small_Group.png",
                alt:
                  "Cover of the From Diagnosis to Goodbye Church Small-Group Facilitator Guide by Derrick Hall",
              },
              {
                id: "senior-living",
                title: "Senior Living Center Facilitator Guide",
                subtitle:
                  "For Compassionate Conversations on Aging, Serious Illness, Grief & Support",
                description:
                  "Created specifically for senior living communities, retirement centers, and activity directors seeking meaningful educational programming for residents and families.",
                cover:
                  "/facilitator-guides/FDTG_Facilitator_Senior_Living_Center.png",
                alt:
                  "Cover of the From Diagnosis to Goodbye Senior Living Center Facilitator Guide by Derrick Hall",
              },
              {
                id: "hospice-staff",
                title: "Hospice Staff Training Facilitator Guide",
                subtitle: "For Interdisciplinary Hospice Care Teams",
                description:
                  "Professional facilitator resource designed to strengthen interdisciplinary teamwork, communication, family support, and compassionate hospice care.",
                cover:
                  "/facilitator-guides/FDTG_Facilitator_Hospice_Staff_Training.png",
                alt:
                  "Cover of the From Diagnosis to Goodbye Hospice Staff Training Facilitator Guide by Derrick Hall",
              },
            ].map((g) => (
              <article
                key={g.id}
                data-testid={`fdtg-facilitator-card-${g.id}`}
                className="flex flex-col bg-white border border-ink-100 rounded-3xl overflow-hidden shadow-[0_18px_60px_-24px_rgba(15,23,42,0.10)] hover:shadow-[0_28px_80px_-24px_rgba(15,23,42,0.18)] transition-shadow duration-300"
              >
                <div className="relative bg-gradient-to-br from-softblue/40 via-white to-secondary/10 border-b border-ink-100 p-5">
                  <img
                    src={g.cover}
                    alt={g.alt}
                    data-testid={`fdtg-facilitator-cover-${g.id}`}
                    loading="lazy"
                    className="w-full h-auto max-h-[380px] object-contain mx-auto drop-shadow-[0_16px_32px_rgba(15,23,42,0.18)] rounded-md"
                  />
                  <span
                    data-testid={`fdtg-facilitator-badge-${g.id}`}
                    aria-label="Status: Coming Soon"
                    className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-secondary text-white text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1.5 rounded-full shadow-md"
                  >
                    <Sparkle size={11} weight="fill" />
                    Coming Soon
                  </span>
                </div>
                <div className="flex-1 flex flex-col p-6">
                  <h4
                    data-testid={`fdtg-facilitator-title-${g.id}`}
                    className="font-heading text-xl text-ink-900 font-medium leading-snug"
                  >
                    {g.title}
                  </h4>
                  <p className="mt-2 font-heading text-sm text-ink-700 italic leading-snug">
                    {g.subtitle}
                  </p>
                  <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-primary font-semibold">
                    By {FLAGSHIP_BOOK.author}
                  </p>
                  <p className="mt-4 text-sm text-ink-700 leading-relaxed flex-1">
                    {g.description}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </SectionShell>

      {/* 6. PARTICIPANT GUIDES */}
      <SectionShell
        id="participant-guides"
        overline="For Learners"
        title="Participant Guides"
        icon={UsersThree}
      >
        <ContentCard testId="fdtg-participant-card">
          <p>
            The <em>{BRAND}</em> Participant Guides are designed to serve as
            personal companions throughout each learning experience. Whether
            attending a workshop, church class, hospice training, or
            community seminar, participants will have practical resources
            that encourage reflection, discussion, and personal growth.
          </p>
          <p>
            Each guide includes note pages, reflection questions, practical
            exercises, conversation prompts, key learning points, personal
            journaling space, and optional reflection exercises or closing
            prayers where appropriate. Participants are encouraged to
            record personal insights, meaningful conversations, and
            practical applications as they journey through the program.
          </p>
          <p>
            Together with the Facilitator Guides, these resources create a
            complete educational experience that equips individuals,
            families, caregivers, healthcare professionals, churches, and
            community organizations to navigate the end-of-life journey
            with greater understanding, compassion, confidence, and hope.
          </p>
        </ContentCard>
      </SectionShell>

      {/* 6b. COMING SOON: COMPASSIONATE REFLECTIONS CARD COLLECTION */}
      <section
        id="reflection-cards"
        data-testid="fdtg-section-reflection-cards"
        className="scroll-mt-28 py-16 sm:py-20 border-t border-ink-100"
        aria-labelledby="fdtg-reflection-cards-heading"
      >
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Companion Collection
            </span>
            <div className="mt-5 flex items-start gap-4">
              <span className="hidden sm:inline-flex items-center justify-center w-14 h-14 shrink-0 rounded-2xl bg-primary/8 text-primary border border-primary/10">
                <Sparkle size={26} weight="duotone" />
              </span>
              <h2
                id="fdtg-reflection-cards-heading"
                data-testid="fdtg-reflection-cards-heading"
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.1]"
              >
                Compassionate Reflections Card Collection
              </h2>
            </div>
            <p
              data-testid="fdtg-reflection-cards-tagline"
              className="mt-6 font-heading italic text-xl sm:text-2xl text-ink-700 leading-snug"
            >
              Small moments of presence. Meaningful words for the journey.
            </p>
          </div>

          <article
            data-testid="fdtg-reflection-cards-card"
            className="mt-12 lg:mt-14 bg-white border border-ink-100 rounded-3xl overflow-hidden shadow-[0_18px_60px_-24px_rgba(15,23,42,0.10)]"
          >
            <div className="grid lg:grid-cols-12 gap-0">
              {/* Full image — object-contain preserves full composition and aspect ratio */}
              <div className="relative lg:col-span-7 bg-gradient-to-br from-softblue/40 via-white to-secondary/10 border-b lg:border-b-0 lg:border-r border-ink-100 p-6 sm:p-8">
                <img
                  src="/collections/reflection-cards.jpg"
                  alt="The Compassionate Reflections Card Collection — five reflection cards inspired by From Diagnosis to Goodbye arranged on a table with a candle, a book, a pen, and a small golden heart."
                  data-testid="fdtg-reflection-cards-image"
                  loading="lazy"
                  className="w-full h-auto object-contain mx-auto drop-shadow-[0_20px_40px_rgba(15,23,42,0.18)] rounded-md"
                />
                <span
                  data-testid="fdtg-reflection-cards-badge"
                  aria-label="Status: Coming Soon"
                  className="absolute top-5 right-5 inline-flex items-center gap-1.5 bg-secondary text-white text-[10px] uppercase tracking-[0.22em] font-bold px-3 py-1.5 rounded-full shadow-md"
                >
                  <Sparkle size={11} weight="fill" />
                  Coming Soon
                </span>
              </div>

              <div className="lg:col-span-5 p-7 sm:p-10 flex flex-col justify-center">
                <p className="text-xs uppercase tracking-[0.22em] text-primary font-semibold">
                  By {FLAGSHIP_BOOK.author}
                </p>
                <p
                  data-testid="fdtg-reflection-cards-description"
                  className="mt-5 text-ink-700 leading-relaxed text-base sm:text-lg"
                >
                  A collection of compassionate reflection cards inspired by{" "}
                  <em>{BRAND}</em>, created to encourage presence, meaningful
                  conversation, comfort, and reflection throughout serious
                  illness, caregiving, end of life, and grief.
                </p>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* 7. SEMINARS & WORKSHOPS */}
      <SectionShell
        id="seminars"
        overline="For Families, Caregivers & Communities"
        title="Seminars & Workshops"
        icon={Presentation}
      >
        <ContentCard testId="fdtg-seminars-card">
          <p>
            <em>{BRAND}</em> seminars and workshops are designed to educate,
            encourage, and equip families, caregivers, faith communities,
            healthcare professionals, and community organizations to
            navigate the end-of-life journey with greater confidence and
            compassion.
          </p>
          <p>
            Each presentation combines practical guidance, real-life
            hospice experience, compassionate communication, and spiritual
            insight to help participants better understand the challenges
            that accompany serious illness, caregiving, grief, and the
            final stages of life. Whether presented in person or virtually,
            every session is designed to foster meaningful conversations,
            strengthen relationships, and provide practical tools that
            participants can immediately apply.
          </p>
          <p>
            Workshop topics, upcoming events, registration information, and
            customized training opportunities will be added as the{" "}
            <em>{BRAND}</em> educational platform continues to grow.
          </p>
        </ContentCard>
      </SectionShell>

      {/* 8. PROFESSIONAL EDUCATION FOR HOSPICE & HEALTHCARE ORGANIZATIONS */}
      <SectionShell
        id="hospice-training"
        overline="For Care Teams"
        title="Professional Education for Hospice & Healthcare Organizations"
        icon={Buildings}
      >
        <ContentCard testId="fdtg-hospice-card">
          <p>
            <em>{BRAND}</em> provides the educational foundation for
            patient-centered programs designed for hospice and healthcare
            professionals. These programs help care teams strengthen
            compassionate communication, preserve dignity, support
            families, and keep the patient&rsquo;s voice present throughout
            serious illness, end-of-life care, and grief.
          </p>
          <p>
            Professional courses and team-training opportunities based on{" "}
            <em>{BRAND}</em> are delivered through the CCCHAPS Chaplain
            Training Academy, including interdisciplinary education for
            healthcare teams and specialized hospice chaplaincy training.
          </p>
          <p>
            Organizations may also request customized workshops,
            presentations, and educational resources based on{" "}
            <em>{BRAND}</em>.
          </p>
          <div className="pt-2">
            <a
              href="https://ccchaps.com/academy"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="fdtg-hospice-ccchaps-cta"
              aria-label="Explore Professional Training at the CCCHAPS Chaplain Training Academy (opens in a new tab)"
              className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold text-base hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
            >
              <GraduationCap size={18} weight="duotone" />
              Explore Professional Training
            </a>
          </div>
        </ContentCard>
      </SectionShell>

      {/* 9. TRAINING FOR CHURCHES & COMMUNITY GROUPS */}
      <SectionShell
        id="church-training"
        overline="For Faith Communities"
        title="Training for Churches & Community Groups"
        icon={Church}
      >
        <ContentCard testId="fdtg-church-card">
          <p>
            <em>{BRAND}</em> equips churches and community organizations to
            become confident, compassionate companions for individuals and
            families facing serious illness, caregiving responsibilities,
            grief, and loss.
          </p>
          <p>
            Whether serving as pastors, deacons, elders, care ministry
            leaders, visitation teams, or lay volunteers, participants
            receive practical guidance that helps them minister with
            greater understanding, confidence, and hope. Training
            emphasizes compassionate presence, effective communication,
            spiritual encouragement, and practical support while helping
            churches build stronger ministries of care within their own
            communities.
          </p>
          <p>
            Church workshops, ministry training events, and community
            education opportunities will continue to expand as new
            resources are released.
          </p>
        </ContentCard>
      </SectionShell>

      {/* 10. SPEAKING ENGAGEMENTS */}
      <SectionShell
        id="speaking"
        overline="On the Platform"
        title="Speaking Engagements"
        icon={Microphone}
      >
        <ContentCard testId="fdtg-speaking-card">
          <p>
            Chaplain Derrick Hall is available to speak for hospice
            organizations, healthcare conferences, churches, caregiver
            events, grief support programs, community organizations,
            leadership gatherings, and professional training events.
          </p>
          <p>
            Drawing upon years of ministry and hospice chaplaincy
            experience, Derrick presents practical, compassionate, and
            engaging sessions that encourage meaningful conversations about
            serious illness, caregiving, grief, hospice, hope, and the
            importance of restoring the patient&rsquo;s voice throughout
            every stage of the journey.
          </p>
          <p>
            Whether delivering keynote presentations, conference breakout
            sessions, leadership workshops, or community seminars, each
            presentation is designed to educate, inspire, and equip
            audiences with practical insights they can immediately apply in
            their personal lives, ministries, and professional roles.
          </p>
          <p>
            Speaking topics, availability, and booking information will
            continue to expand as the <em>{BRAND}</em> educational platform
            grows.
          </p>
        </ContentCard>
      </SectionShell>

      {/* 11. LICENSING OPPORTUNITIES */}
      <SectionShell
        id="licensing"
        overline="Bring the Program to Your Organization"
        title="Licensing Opportunities"
        icon={Handshake}
      >
        <ContentCard testId="fdtg-licensing-card">
          <p>
            Organizations interested in implementing the <em>{BRAND}</em>{" "}
            educational platform may license selected training materials,
            facilitator resources, and educational programs for use within
            their own organizations.
          </p>
          <p>
            Licensing opportunities are being developed for hospice
            organizations, healthcare providers, churches, educational
            institutions, community organizations, and other groups seeking
            a structured, compassionate approach to end-of-life education.
          </p>
          <p>
            Future licensing options may include facilitator guides,
            participant materials, organizational training packages,
            seminar curricula, workshop resources, and other educational
            materials designed to help organizations confidently deliver
            the <em>{BRAND}</em> program while preserving the integrity,
            mission, and educational standards of the platform.
          </p>
          <p>
            Whether your organization is interested in staff development,
            caregiver education, church training, or community outreach, we
            welcome the opportunity to discuss how the <em>{BRAND}</em>{" "}
            educational platform can be customized to meet your needs.
          </p>

          <div className="pt-2 flex flex-wrap items-center gap-4">
            <Link
              to="/contact?topic=licensing"
              data-testid="fdtg-licensing-inquire-btn"
              aria-label="Inquire about licensing the From Diagnosis to Goodbye program"
              className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/25"
            >
              <Handshake size={18} weight="duotone" />
              Inquire About Licensing
            </Link>
            <a
              href="/licensing/From_Diagnosis_to_Goodbye_Licensing_Handbook_v1_Draft.pdf"
              download
              data-testid="fdtg-licensing-handbook-btn"
              aria-label="Download the From Diagnosis to Goodbye Licensing Handbook, Version 1.0 (Draft) PDF"
              className="inline-flex items-center gap-2 border-2 border-primary text-primary rounded-full px-6 py-3.5 font-semibold hover:bg-primary/5 transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/25"
            >
              <FilePdf size={18} weight="duotone" />
              Download Licensing Handbook (PDF)
            </a>
            <span
              data-testid="fdtg-licensing-handbook-version"
              className="text-xs uppercase tracking-[0.22em] text-ink-500 font-semibold"
            >
              Version 1.0 (Draft)
            </span>
          </div>
        </ContentCard>
      </SectionShell>

      {/* 12. TESTIMONIALS */}
      <SectionShell
        id="testimonials"
        overline="Voices from the Path"
        title="Testimonials"
        icon={Quotes}
      >
        <div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="fdtg-testimonials-grid"
        >
          {[
            {
              id: "michelle-b",
              name: "Michelle B.",
              role: "Daughter",
              quote:
                "The day I started reading your book, I couldn\u2019t put it down. I only wish I had this book before my mother died\u2014I would have done things differently. Thank you for providing a guide that helps families understand what matters most during the end-of-life journey.",
            },
            {
              id: "steve-c",
              name: "Steve C.",
              role: "Son",
              quote:
                "At first, I was skeptical. I began reading the preview on Amazon, and I couldn\u2019t stop. The author was giving me exactly what I needed, so I purchased several copies for my family. This book is meeting our needs as we say goodbye to our dad. Thank you.",
            },
            {
              id: "chaplain-frank",
              name: "Chaplain Frank",
              role: "Hospice Chaplain",
              quote:
                "A compact and wonderful guide filled with practical wisdom. The guidance throughout the book is exceptional, and the appendices are thoughtfully organized and extremely useful. This is an outstanding resource that helps chaplains serve families with greater confidence, compassion, and effectiveness.",
            },
          ].map((t) => {
            const initials = t.name
              .split(/\s+/)
              .map((p) => p[0])
              .filter(Boolean)
              .slice(0, 2)
              .join("")
              .toUpperCase();
            return (
              <figure
                key={t.id}
                data-testid={`fdtg-testimonial-${t.id}`}
                className="relative flex flex-col bg-white border border-ink-100 rounded-3xl p-8 shadow-[0_18px_60px_-24px_rgba(15,23,42,0.08)] hover:shadow-[0_28px_80px_-24px_rgba(15,23,42,0.14)] transition-shadow duration-300"
              >
                <Quotes
                  size={32}
                  weight="fill"
                  className="text-secondary/40"
                  aria-hidden="true"
                />
                <blockquote
                  data-testid={`fdtg-testimonial-quote-${t.id}`}
                  className="mt-4 text-ink-800 italic leading-relaxed flex-1"
                >
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-6 pt-6 border-t border-ink-100 flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="w-10 h-10 rounded-full bg-primary/10 border border-primary/15 text-primary flex items-center justify-center font-semibold text-sm"
                  >
                    {initials}
                  </span>
                  <div>
                    <p
                      data-testid={`fdtg-testimonial-name-${t.id}`}
                      className="text-sm font-semibold text-ink-900"
                    >
                      {t.name}
                    </p>
                    <p className="text-xs text-ink-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            );
          })}
        </div>

        <p
          data-testid="fdtg-testimonials-note"
          className="mt-10 text-center text-sm text-ink-500 italic max-w-2xl mx-auto leading-relaxed"
        >
          As additional feedback is received from readers, families, hospice
          professionals, churches, and seminar participants, testimonials
          will continue to be added to this section.
        </p>
      </SectionShell>

      {/* PUBLISHER & AUTHOR ATTRIBUTION */}
      <PublisherAttribution testIdPrefix="fdtg-attribution" />

      {/* 12. CONTACT / BOOK DERRICK HALL */}
      <section
        id="contact"
        data-testid="fdtg-section-contact"
        className="scroll-mt-28 py-20 sm:py-28 border-t border-ink-100 bg-softblue/40"
      >
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Book Derrick Hall
            </span>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.1]">
              Bring {BRAND} to your organization.
            </h2>
            <p className="mt-6 text-ink-700 text-lg leading-relaxed">
              For speaking engagements, seminars, hospice team training, and
              church or community group workshops, please reach out through
              our ministry contact page and select the option that best
              describes your inquiry.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact?topic=book-derrick"
                data-testid="fdtg-contact-cta"
                className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold text-base hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
              >
                <EnvelopeSimple size={18} weight="duotone" />
                Book Derrick Hall
              </Link>
              <Link
                to="/contact?topic=workshop-request"
                data-testid="fdtg-request-cta"
                className="inline-flex items-center gap-2 border-2 border-primary text-primary rounded-full px-7 py-3.5 font-semibold hover:bg-primary/5 transition-colors"
              >
                <GraduationCap size={18} weight="duotone" />
                Request Training Details
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FromDiagnosisToGoodbye;
